import { useState, type ReactNode } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'

/** Visual do chip de filtro ("Empresa: Todas ▾"), compartilhado com o
 * gatilho do `MultiSelect` na variante `chip`. */
export const CHIP_TRIGGER_CLASS =
  'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border border-border-subtle bg-surface px-3.5 text-[13px] text-body transition hover:border-border-strong'

/**
 * Camada que fecha o menu ao clicar fora — mesma técnica do `MultiSelect`: a
 * camada recebe o clique e o interrompe, para ele não "vazar" para o que está
 * atrás (ex.: uma linha clicável da tabela).
 */
function DismissLayer({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="presentation"
      className="fixed inset-0 z-[199]"
      onClick={(e) => {
        e.stopPropagation()
        onClose()
      }}
    />
  )
}

interface ChipSelectProps<V extends string> {
  /** Rótulo antes do valor ("Ordenar", "Categoria"). */
  label: string
  options: ReadonlyArray<{ value: V; label: string }>
  value: V | null
  onChange: (value: V | null) => void
  /** Texto do valor quando nada está escolhido ("Todas"); sem ele, o chip
   * mostra só o rótulo. */
  allLabel?: string
}

/** Chip com menu de escolha única ("Categoria ▾"). Com `allLabel`, oferece
 * a opção de limpar. */
export function ChipSelect<V extends string>({
  label,
  options,
  value,
  onChange,
  allLabel,
}: ChipSelectProps<V>) {
  const [open, setOpen] = useState(false)
  const selected = options.find((o) => o.value === value)
  const text = selected
    ? `${label}: ${selected.label}`
    : allLabel
      ? `${label}: ${allLabel}`
      : label
  const pick = (v: V | null) => {
    onChange(v)
    setOpen(false)
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={CHIP_TRIGGER_CLASS}
      >
        {text}
        <ChevronDown className="h-3.5 w-3.5 text-subtle" />
      </button>
      {open && (
        <>
          <DismissLayer onClose={() => setOpen(false)} />
          <ul
            role="listbox"
            className="absolute left-0 top-full z-[200] mt-1 min-w-[13rem] rounded-lg border border-border-subtle bg-surface p-1 shadow-xl ring-1 ring-black/5"
          >
            {allLabel && (
              <MenuOption active={value === null} onClick={() => pick(null)}>
                {allLabel}
              </MenuOption>
            )}
            {options.map((o) => (
              <MenuOption
                key={o.value}
                active={o.value === value}
                onClick={() => pick(o.value)}
              >
                {o.label}
              </MenuOption>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function MenuOption({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <li role="option" aria-selected={active}>
      <button
        type="button"
        onClick={onClick}
        className={`flex w-full items-center justify-between gap-3 rounded-md px-2.5 py-1.5 text-left text-[13px] transition ${
          active
            ? 'bg-blue-50 font-medium text-blue-800 dark:bg-blue-900/25 dark:text-blue-200'
            : 'text-body hover:bg-surface-muted'
        }`}
      >
        {children}
        {active && <Check className="h-3.5 w-3.5 shrink-0" />}
      </button>
    </li>
  )
}

/** Menu do "+ Filtro": lista os filtros que ainda podem ser adicionados. */
export function AddFilterMenu<K extends string>({
  options,
  onAdd,
}: {
  options: ReadonlyArray<{ key: K; label: string }>
  onAdd: (key: K) => void
}) {
  const [open, setOpen] = useState(false)
  if (options.length === 0) return null
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex h-8 items-center gap-1 whitespace-nowrap rounded-full border border-dashed border-border-strong px-3.5 text-[13px] text-muted transition hover:border-gray-400 hover:text-body"
      >
        <span aria-hidden className="text-base leading-none">+</span>
        Filtro
      </button>
      {open && (
        <>
          <DismissLayer onClose={() => setOpen(false)} />
          <ul className="absolute left-0 top-full z-[200] mt-1 min-w-[12rem] rounded-lg border border-border-subtle bg-surface p-1 shadow-xl ring-1 ring-black/5">
            {options.map((o) => (
              <li key={o.key}>
                <button
                  type="button"
                  onClick={() => {
                    onAdd(o.key)
                    setOpen(false)
                  }}
                  className="w-full rounded-md px-2.5 py-1.5 text-left text-[13px] text-body transition hover:bg-surface-muted"
                >
                  {o.label}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

/** Filtro aplicado e removível ("Prazo: vencido ×"), em vermelho claro. */
export function ActiveFilterChip({
  children,
  onRemove,
  removeLabel,
}: {
  children: ReactNode
  onRemove: () => void
  removeLabel: string
}) {
  return (
    <span className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border border-red-200 bg-red-50 pl-3.5 pr-2 text-[13px] text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300">
      {children}
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="rounded-full p-0.5 transition hover:bg-red-100 dark:hover:bg-red-900/40"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  )
}
